import { StoreConfig } from '../../config/store-config';
import {
    ReturnPackage,
    isShopeeFailedDelivery,
    isTiktokFailedDelivery,
    normalizeLazadaFailedDelivery,
    normalizeLazadaReverseOrder,
    normalizeShopeeFailedDelivery,
    normalizeShopeeReturn,
    normalizeTiktokFailedDelivery,
    normalizeTiktokReturn,
} from './return-package';

// The return collector's index (lnwshop-insight-functions/functions/returns,
// collection management-console.return_packages): one RAW platform record
// per return or failed delivery, stored as exactly the input this file's
// normalizers take — so this console stays the one owner of every mapping,
// and a normalizer fix applies to every stored record at once.

export type IndexKind =
    | 'shopee_return'
    | 'shopee_failed_delivery'
    | 'lazada_reverse'
    | 'lazada_failed_delivery'
    | 'tiktok_return'
    | 'tiktok_failed_delivery';

export interface IndexDoc {
    _id: string;
    storeId: string;
    kind: IndexKind;
    orderNo: string;
    // The normalizer's createdAt, epoch seconds.
    eventAt: number | null;
    payload: any;
    // The last collector run could not fetch this record's detail /
    // tracking; what is stored is from before (or the list row alone).
    fetchError: string | null;
}

// Each store's collector state (collection return_collector_state).
export interface IndexState {
    _id: string;
    // ISO; the last run that read both the returns and failed deliveries.
    lastSuccessAt?: string;
    // Epoch seconds: how far back the index reaches for this store.
    coversFrom?: number;
    returnsError?: string | null;
    failedDeliveryError?: string | null;
}

export interface IndexResponse {
    packages: IndexDoc[];
    state: IndexState[];
    now: number;
}

// The package a stored record describes, through the same normalizer the
// live fetch uses — or null when it is not one the tracker lists (the
// collector keeps a superset of failed-delivery candidates).
export const packageFromIndex = (
    store: StoreConfig,
    doc: IndexDoc,
): ReturnPackage | null => {
    const payload = doc.payload ?? {};
    switch (doc.kind) {
        case 'shopee_return':
            return normalizeShopeeReturn(
                store,
                payload.listRow,
                undefined,
                payload.forward,
            );
        case 'shopee_failed_delivery':
            return isShopeeFailedDelivery(payload.order)
                ? normalizeShopeeFailedDelivery(
                      store,
                      payload.order,
                      payload.forward,
                  )
                : null;
        case 'lazada_reverse':
            return normalizeLazadaReverseOrder(store, payload.row);
        case 'lazada_failed_delivery':
            return normalizeLazadaFailedDelivery(
                store,
                payload.order,
                payload.items,
            );
        case 'tiktok_return':
            return normalizeTiktokReturn(store, payload.row);
        case 'tiktok_failed_delivery':
            return isTiktokFailedDelivery(payload.order)
                ? normalizeTiktokFailedDelivery(store, payload.order)
                : null;
        default:
            return null;
    }
};
